import moment from 'moment';

/**
 * Determine x-axis unit based on bucket date difference
 */
export function getXAxisUnit(bucketStart, bucketEnd) {
  const start = moment(bucketStart);
  const end = moment(bucketEnd);
  const diffDays = end.diff(start, 'days');

  if (diffDays <= 1) return 'day';
  if (diffDays <= 7) return 'week';
  return 'month';
}

/**
 * Format x-axis label based on unit
 */
export function formatXAxisLabel(bucketStart, bucketEnd, unit) {
  const start = moment(bucketStart);
  const end = moment(bucketEnd);

  switch (unit) {
    case 'day':
      return start.format('MMM D');
    case 'week':
      return `${start.format('MMM D')} - ${end.format('MMM D')}`;
    case 'month':
      return start.format('MMM YYYY');
    default:
      return start.format('MMM D');
  }
}

/**
 * Calculate dynamic y-axis config from data values
 */
export function calculateYAxisConfig(values, { allowNegative = false } = {}) {
  if (!values.length) return { min: 0, max: 10, stepSize: 2 };

  let dataMin = Math.min(...values);
  let dataMax = Math.max(...values);

  if (!allowNegative) dataMin = 0;

  // Add padding to prevent values from being cut off (10% padding)
  const paddingFactor = 0.1;
  if (dataMax > 0) {
    dataMax = dataMax * (1 + paddingFactor);
  }
  if (dataMin < 0) {
    dataMin = dataMin * (1 + paddingFactor);
  }

  // Calculate step size for positive range (0 to max)
  function calculateStepSize(range) {
    if (range <= 10) return 1;
    else if (range <= 50) return 10;
    else if (range <= 100) return 20;
    else if (range <= 500) return 100;
    else if (range <= 1000) return 200;
    else if (range <= 5000) return 1000;
    else if (range <= 10000) return 2000;
    else if (range <= 50000) return 10000;
    else if (range <= 100000) return 20000;
    else return Math.ceil(range / 10 / 10000) * 10000;
  }

  const positiveRange = Math.max(0, dataMax);
  const negativeRange = Math.abs(Math.min(0, dataMin));

  const positiveStepSize = calculateStepSize(positiveRange);
  const negativeStepSize = calculateStepSize(negativeRange);

  // Use the larger step size for both positive and negative
  const stepSize = Math.max(positiveStepSize, negativeStepSize);

  // Calculate min and max to ensure 0 is always a tick
  const min = allowNegative && dataMin < 0 
    ? (Math.floor(dataMin / stepSize) - 1) * stepSize 
    : 0;
  const max = dataMax > 0 
    ? Math.ceil(dataMax / stepSize) * stepSize 
    : (allowNegative ? 0 : stepSize);

  return { min, max, stepSize };
}

/**
 * Process Patient Growth chart data
 */
export function processPatientGrowthData(series) {
  if (!series?.length) return { labels: [], data: [], yAxisConfig: {} };

  const unit = getXAxisUnit(series[0].bucket_start, series[0].bucket_end);
  const labels = [];
  const data = [];

  series.forEach((item) => {
    labels.push(formatXAxisLabel(item.bucket_start, item.bucket_end, unit));
    data.push(item.value);
  });

  const yAxisConfig = calculateYAxisConfig(data);
  return { labels, data, yAxisConfig, unit };
}

/**
 * Process Patient Engagement chart data (engaged/not_engaged counts)
 */
export function processEngagementData(series) {
  if (!series?.length) return { labels: [], engagedData: [], notEngagedData: [], yAxisConfig: {} };

  const unit = getXAxisUnit(series[0].bucket_start, series[0].bucket_end);
  const labels = [];
  const engagedData = [];
  const notEngagedData = [];

  series.forEach((item) => {
    labels.push(formatXAxisLabel(item.bucket_start, item.bucket_end, unit));
    engagedData.push(item.engaged_count || 0);
    notEngagedData.push(item.not_engaged_count || 0);
  });

  const allValues = [...engagedData, ...notEngagedData];
  const yAxisConfig = calculateYAxisConfig(allValues);
  return { labels, engagedData, notEngagedData, yAxisConfig, unit };
}

/**
 * Process Orders and Revenue chart data (stacked positive/negative)
 */
export function processOrderRevenueData(buckets) {
  if (!buckets?.length) {
    return {
      labels: [],
      created: {},
      canceled: {},
      net: [],
      yAxisConfig: {},
    };
  }
  const createdFields = [
    'created_clinic_revenue_lab_markup',
    'created_clinic_revenue_practice_products',
    'created_clinic_revenue_consultation_fee',
    'created_payments_to_vibrant_lab_patient_pay',
    'created_payments_to_vibrant_lab_provider_pay',
  ];

  const canceledFields = [
    'canceled_clinic_revenue_lab_markup',
    'canceled_clinic_revenue_practice_products',
    'canceled_clinic_revenue_consultation_fee',
    'canceled_payments_to_vibrant_lab_patient_pay',
    'canceled_payments_to_vibrant_lab_provider_pay',
  ];

  const unit = getXAxisUnit(buckets[0].bucket_start, buckets[0].bucket_end);
  const labels = [];

  const created = {};
  const canceled = {};
  createdFields.forEach((f) => (created[f] = []));
  canceledFields.forEach((f) => (canceled[f] = []));
  const net = [];

  buckets.forEach((bucket) => {
    labels.push(formatXAxisLabel(bucket.bucket_start, bucket.bucket_end, unit));

    createdFields.forEach((f) => {
      created[f].push(bucket[f] || 0);
    });

    canceledFields.forEach((f) => {
      canceled[f].push(-Math.abs(bucket[f] || 0));
    });

    net.push(bucket.net_order_amount);
  });

  // Calculate y-axis from sums at each bucket
  const allPositive = [];
  const allNegative = [];

  for (let i = 0; i < buckets.length; i++) {
    createdFields.forEach((f) => {
      allPositive.push(created[f][i]);
    });
    
    canceledFields.forEach((f) => {
      allNegative.push(canceled[f][i]);
    });
  }

  const allValues = [...allPositive, ...allNegative];
  const yAxisConfig = calculateYAxisConfig(allValues, { allowNegative: true });

  return { labels, created, canceled, net, yAxisConfig, unit };
}

/**
 * Display-friendly field labels
 */
export const REVENUE_FIELD_LABELS = {
  created_orders_amount: 'Created Orders',
  created_clinic_revenue_lab_markup: 'Lab Markup',
  created_clinic_revenue_practice_products: 'Practice Products',
  created_clinic_revenue_consultation_fee: 'Consultation Fee',
  created_payments_to_vibrant_lab_patient_pay: 'Patient Pay',
  created_payments_to_vibrant_lab_provider_pay: 'Provider Pay',
  canceled_orders_amount: 'Canceled Orders',
  canceled_clinic_revenue_lab_markup: 'Canceled Lab Markup',
  canceled_clinic_revenue_practice_products: 'Canceled Practice Products',
  canceled_clinic_revenue_consultation_fee: 'Canceled Consultation Fee',
  canceled_payments_to_vibrant_lab_patient_pay: 'Canceled Patient Pay',
  canceled_payments_to_vibrant_lab_provider_pay: 'Canceled Provider Pay',
};

export const REVENUE_FIELD_ORDER = {
  created: [
    'created_clinic_revenue_lab_markup',
    'created_payments_to_vibrant_lab_patient_pay',
    'created_payments_to_vibrant_lab_provider_pay',
    'created_clinic_revenue_practice_products',
    'created_clinic_revenue_consultation_fee'
  ],
  canceled: [
    'canceled_clinic_revenue_lab_markup',
    'canceled_payments_to_vibrant_lab_patient_pay',
    'canceled_payments_to_vibrant_lab_provider_pay',
    'canceled_clinic_revenue_practice_products',
    'canceled_clinic_revenue_consultation_fee'
  ]
};

/**
 * Color palette for chart series
 */
export const CHART_COLORS = {
  engaged: {
    base: '#25B8C2',
    hover: '#7CD4DA'
  },
  notEngaged: {
    base: '#E0E0E0',
    hover: '#C4C4C4'
  },
  created: {
    created_clinic_revenue_lab_markup: {
      base: '#81B0E1',
      hover: '#9AC0E7'
    },
    created_clinic_revenue_practice_products: {
      base: '#8BBDC7',
      hover: '#A2CAD2'
    },
    created_clinic_revenue_consultation_fee: {
      base: '#25B8C2',
      hover: '#51C6CE'
    },
    created_payments_to_vibrant_lab_patient_pay: {
      base: '#5587BC',
      hover: '#779FC9'
    },
    created_payments_to_vibrant_lab_provider_pay: {
      base: '#004879',
      hover: '#336D94'
    }
  },
  canceled: {
    canceled_clinic_revenue_lab_markup: {
      base: '#979797',
      hover: '#C4C4C4'
    },
    canceled_clinic_revenue_practice_products: {
      base: '#979797',
      hover: '#C4C4C4'
    },
    canceled_clinic_revenue_consultation_fee: {
      base: '#979797',
      hover: '#C4C4C4'
    },
    canceled_payments_to_vibrant_lab_patient_pay: {
      base: '#979797',
      hover: '#C4C4C4'
    },
    canceled_payments_to_vibrant_lab_provider_pay: {
      base: '#979797',
      hover: '#C4C4C4'
    }
  },
  lineChart: '#25B8C2',
};
